import React, { useEffect, useState, useCallback } from 'react';
import { formatDistanceToNow, isToday, isYesterday, isThisWeek, parseISO } from 'date-fns';
import { ChatSession } from '../../_config/interfaces/interfaces';
import { useChatHistoryApi } from '../../store/api/hooks/chatHistoryApiHook';

interface Props {
  isOpen: boolean;
  activeSession: string | null;
  onSelectSession: (id: string) => void;
  onNewChat: () => void;
  onClose: () => void;
  refreshTrigger: number;
}

function groupSessions(sessions: ChatSession[]) {
  const today: ChatSession[] = [];
  const yesterday: ChatSession[] = [];
  const thisWeek: ChatSession[] = [];
  const older: ChatSession[] = [];

  sessions.forEach((s) => {
    const d = parseISO(s.created_at);
    if (isToday(d)) today.push(s);
    else if (isYesterday(d)) yesterday.push(s);
    else if (isThisWeek(d)) thisWeek.push(s);
    else older.push(s);
  });

  return { today, yesterday, thisWeek, older };
}

function relativeTime(iso: string) {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

const SkeletonItem = () => (
  <div className="animate-pulse h-10 bg-gray-100 rounded-xl mb-2" />
);

const ChatHistorySidebar: React.FC<Props> = ({ isOpen, activeSession, onSelectSession, onNewChat, onClose, refreshTrigger }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const chatHistoryApi = useChatHistoryApi();

  const loadSessions = useCallback(() => {
    setLoading(true);
    chatHistoryApi.getSessions(
      (_msg: any, resp: any) => {
        // appApiCallHook passes (message, data) — resp is data.data (the array)
        setSessions(Array.isArray(resp) ? resp : []);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions, refreshTrigger]);

  const handleDelete = (e: React.MouseEvent, session_id: string) => {
    e.stopPropagation();
    setConfirmDelete(session_id);
  };

  const confirmDeleteSession = (e: React.MouseEvent, session_id: string) => {
    e.stopPropagation();
    chatHistoryApi.deleteSession(
      session_id,
      (_msg: any, _resp: any) => {
        setSessions((prev) => prev.filter((s) => s.session_id !== session_id));
        if (activeSession === session_id) onNewChat();
        setConfirmDelete(null);
      },
      () => setConfirmDelete(null)
    );
  };

  const groups = groupSessions(sessions);

  const renderSession = (s: ChatSession) => {
    const isActive = activeSession === s.session_id;
    const isConfirming = confirmDelete === s.session_id;

    return (
      <div
        key={s.session_id}
        onClick={() => onSelectSession(s.session_id)}
        className={`group relative flex items-center justify-between px-3 py-2.5 mb-1 rounded-xl cursor-pointer transition-all
          ${isActive
            ? 'bg-blue-50 border-l-2 border-blue-500'
            : 'hover:bg-gray-50 border-l-2 border-transparent'
          }`}
      >
        <div className="flex-1 min-w-0 pr-2">
          <p className="text-sm text-gray-700 truncate">
            {s.title || 'New chat'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{relativeTime(s.created_at)}</p>
        </div>

        {isConfirming ? (
          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => confirmDeleteSession(e, s.session_id)}
              className="text-xs bg-red-400 text-white px-1.5 py-0.5 rounded hover:bg-red-500"
            >
              Yes
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
              className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded hover:bg-gray-300"
            >
              No
            </button>
          </div>
        ) : (
          <button
            onClick={(e) => handleDelete(e, s.session_id)}
            className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-red-400 p-1"
            title="Delete"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const renderGroup = (label: string, items: ChatSession[]) => {
    if (!items.length) return null;
    return (
      <div key={label} className="mb-4">
        <p className="text-xs text-gray-400 uppercase tracking-wide px-3 mb-1">{label}</p>
        {items.map(renderSession)}
      </div>
    );
  };

  return (
    <div className={`bg-white flex flex-col shrink-0 transition-all duration-300 overflow-hidden ${isOpen ? `w-64 border-r border-gray-100 mobile-chat-sidebar-open` : 'w-0'}`}>
      {/* New Chat + mobile close button */}
      <div className="px-4 pt-4 pb-2 flex gap-2 items-center ipad-padding-left">
        <button
          onClick={onNewChat}
          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 rounded-xl transition-colors"
        >
          + New Chat
        </button>
        <button
          onClick={onClose}
          className="shrink-0 p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors hidden-above-980"
          title="Close"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {loading ? (
          <>
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
          </>
        ) : sessions.length === 0 ? (
          <p className="text-xs text-gray-400 text-center mt-6 px-4">No previous chats yet</p>
        ) : (
          <>
            {renderGroup('Today', groups.today)}
            {renderGroup('Yesterday', groups.yesterday)}
            {renderGroup('This Week', groups.thisWeek)}
            {renderGroup('Older', groups.older)}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatHistorySidebar;
